import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api, { automacaoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AutomacaoValidacaoOk.css';

const LOGS_POR_PAGINA = 10;
const RUNS_POR_PAGINA = 10;

const AutomacaoValidacaoOk = () => {
  const { isAdminOrDiretor } = useAuth();
  const [estado, setEstado] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsPage, setRunsPage] = useState(1);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [testVendaId, setTestVendaId] = useState('2141');
  const [error, setError] = useState('');
  const [logsTotal, setLogsTotal] = useState(0);
  const logsEndRef = useRef(null);
  const lastLogIdRef = useRef(0);

  const apiBase = (api.defaults.baseURL || '').replace(/\/$/, '');
  const screenshotUrl = (path) => (path ? `${apiBase}${path}` : null);

  const loadData = useCallback(async () => {
    try {
      const [estadoData, runsData] = await Promise.all([
        automacaoService.getEstadoAtual(),
        automacaoService.listRuns(100)
      ]);
      setEstado(estadoData);
      setRuns(Array.isArray(runsData) ? runsData : []);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar dados da automação');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdminOrDiretor()) return undefined;
    loadData();
    const ms = estado?.em_execucao ? 2000 : 5000;
    const interval = setInterval(loadData, ms);
    return () => clearInterval(interval);
  }, [isAdminOrDiretor, loadData, estado?.em_execucao]);

  const loadLogs = useCallback(async (runId, { incremental = false } = {}) => {
    if (!runId) {
      setLogs([]);
      setLogsTotal(0);
      lastLogIdRef.current = 0;
      return;
    }
    try {
      if (incremental && lastLogIdRef.current > 0) {
        const data = await automacaoService.listLogs(runId, { since_id: lastLogIdRef.current });
        const novos = Array.isArray(data?.logs) ? data.logs : [];
        if (novos.length > 0) {
          setLogs((prev) => [...prev, ...novos]);
          lastLogIdRef.current = novos[novos.length - 1].id;
        }
        if (typeof data?.total === 'number') setLogsTotal(data.total);
        return;
      }

      const data = await automacaoService.listLogs(runId, { tail: 1, limit: 5000 });
      const lista = Array.isArray(data?.logs)
        ? data.logs
        : (Array.isArray(data) ? data : []);
      setLogs(lista);
      lastLogIdRef.current = lista.length ? lista[lista.length - 1].id : 0;
      setLogsTotal(typeof data?.total === 'number' ? data.total : lista.length);
    } catch {
      if (!incremental) {
        setLogs([]);
        setLogsTotal(0);
        lastLogIdRef.current = 0;
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedRunId) {
      setLogs([]);
      setLogsTotal(0);
      lastLogIdRef.current = 0;
      return undefined;
    }
    lastLogIdRef.current = 0;
    loadLogs(selectedRunId, { incremental: false });
    const ms = estado?.em_execucao ? 1500 : 3000;
    const interval = setInterval(() => {
      const incremental = Boolean(estado?.em_execucao && lastLogIdRef.current > 0);
      loadLogs(selectedRunId, { incremental });
    }, ms);
    return () => clearInterval(interval);
  }, [selectedRunId, loadLogs, estado?.em_execucao]);

  useEffect(() => {
    setLogsPage(1);
  }, [selectedRunId]);

  useEffect(() => {
    if (estado?.em_execucao && logs.length > 0) {
      setLogsPage(Math.max(1, Math.ceil(logs.length / LOGS_POR_PAGINA)));
    }
  }, [logs.length, estado?.em_execucao]);

  const totalRunsPages = Math.max(1, Math.ceil(runs.length / RUNS_POR_PAGINA));
  const runsPaginaAtual = Math.min(Math.max(1, runsPage), totalRunsPages);
  const runsPaginados = runs.slice(
    (runsPaginaAtual - 1) * RUNS_POR_PAGINA,
    runsPaginaAtual * RUNS_POR_PAGINA
  );

  useEffect(() => {
    if (runsPaginaAtual !== runsPage) {
      setRunsPage(runsPaginaAtual);
    }
  }, [runsPaginaAtual, runsPage]);

  const totalLogsPages = Math.max(1, Math.ceil(logs.length / LOGS_POR_PAGINA));
  const paginaAtual = Math.min(Math.max(1, logsPage), totalLogsPages);
  const logsPaginados = logs.slice(
    (paginaAtual - 1) * LOGS_POR_PAGINA,
    paginaAtual * LOGS_POR_PAGINA
  );

  useEffect(() => {
    if (paginaAtual !== logsPage) {
      setLogsPage(paginaAtual);
    }
  }, [paginaAtual, logsPage]);

  useEffect(() => {
    if (estado?.em_execucao && logsEndRef.current && paginaAtual === totalLogsPages) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, estado?.em_execucao, paginaAtual, totalLogsPages]);

  useEffect(() => {
    if (estado?.run?.id && estado?.em_execucao) {
      setSelectedRunId(estado.run.id);
    }
  }, [estado?.run?.id, estado?.em_execucao]);

  const handleIniciar = async (vendaId = null) => {
    try {
      setStarting(true);
      setError('');
      const data = await automacaoService.iniciarValidacaoOk(vendaId);
      if (data?.run?.id) {
        setSelectedRunId(data.run.id);
      }
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao iniciar automação');
    } finally {
      setStarting(false);
    }
  };

  const handleCancelar = async () => {
    try {
      await automacaoService.cancelarValidacaoOk();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao cancelar');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  if (!isAdminOrDiretor()) {
    return (
      <Layout>
        <div className="automacao-page"><p>Acesso negado.</p></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="automacao-page">
        <div className="page-header">
          <h1>Automação Validação OK → NEO</h1>
          <p>Cadastro automático de vendas CPF no NEO SALES (08:00 e 23:00 automáticos; 12:30 com confirmação do BKO + disparo manual).</p>
        </div>

        {error && <div className="automacao-error">{error}</div>}

        <div className="automacao-status-card">
          <h2>Status atual</h2>
          {loading ? (
            <p>Carregando...</p>
          ) : (
            <>
              <p className={`automacao-badge ${estado?.em_execucao ? 'running' : 'idle'}`}>
                {estado?.em_execucao ? 'Em execução' : 'Parada'}
              </p>
              {estado?.run && (
                <div className="automacao-run-live">
                  <p><strong>Run #{estado.run.id}</strong> — {estado.run.mensagem_atual}</p>
                  <p>Sucesso: {estado.run.qtd_sucesso || 0} | Erro: {estado.run.qtd_erro || 0} | Total: {estado.run.qtd_total || 0}</p>
                  {estado.run.venda_atual_cpf && (
                    <p>CPF atual: {estado.run.venda_atual_cpf}</p>
                  )}
                </div>
              )}
              <div className="automacao-test-box">
                <label htmlFor="test-venda-id">Testar venda específica (ID)</label>
                <div className="automacao-test-row">
                  <input
                    id="test-venda-id"
                    type="number"
                    min="1"
                    value={testVendaId}
                    onChange={(e) => setTestVendaId(e.target.value)}
                    placeholder="Ex: 2141"
                    disabled={starting || estado?.em_execucao}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => handleIniciar(testVendaId || null)}
                    disabled={starting || estado?.em_execucao || !testVendaId}
                  >
                    {starting ? 'Iniciando...' : 'Rodar só esta venda'}
                  </button>
                </div>
                <p className="automacao-test-hint">
                  A venda precisa estar em <strong>validacao_ok</strong>. No modo teste, cada clique gera
                  {' '}<strong>screenshot</strong> nos logs — abra a execução ao lado e acompanhe passo a passo.
                </p>
              </div>
              <div className="automacao-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => handleIniciar()}
                  disabled={starting || estado?.em_execucao}
                >
                  {starting ? 'Iniciando...' : 'Rodar fila completa'}
                </button>
                {estado?.em_execucao && (
                  <button type="button" className="btn-secondary" onClick={handleCancelar}>
                    Solicitar cancelamento
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="automacao-grid">
          <div className="automacao-runs">
            <h2>Histórico de execuções</h2>
            {runs.length === 0 ? (
              <p>Nenhuma execução registrada.</p>
            ) : (
              <>
                <div className="automacao-pagination-info">
                  <span>
                    Mostrando {((runsPaginaAtual - 1) * RUNS_POR_PAGINA) + 1} a{' '}
                    {Math.min(runsPaginaAtual * RUNS_POR_PAGINA, runs.length)} de {runs.length} execuções
                  </span>
                  <span className="automacao-per-page">{RUNS_POR_PAGINA} por página</span>
                </div>
                <table className="automacao-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Início</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>OK</th>
                      <th>Erro</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {runsPaginados.map((run) => (
                      <tr key={run.id} className={selectedRunId === run.id ? 'selected' : ''}>
                        <td>{run.id}</td>
                        <td>{formatDate(run.inicio)}</td>
                        <td>{run.status}</td>
                        <td>{run.qtd_total}</td>
                        <td>{run.qtd_sucesso}</td>
                        <td>{run.qtd_erro}</td>
                        <td>
                          <button type="button" className="btn-sm btn-secondary" onClick={() => setSelectedRunId(run.id)}>
                            Logs
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="automacao-pagination">
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setRunsPage(1)}
                    disabled={runsPaginaAtual === 1}
                    aria-label="Primeira página"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setRunsPage((p) => Math.max(1, p - 1))}
                    disabled={runsPaginaAtual === 1}
                  >
                    Anterior
                  </button>
                  <span className="automacao-pagination-pages">
                    Página {runsPaginaAtual} de {totalRunsPages}
                  </span>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setRunsPage((p) => Math.min(totalRunsPages, p + 1))}
                    disabled={runsPaginaAtual === totalRunsPages}
                  >
                    Próxima
                  </button>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setRunsPage(totalRunsPages)}
                    disabled={runsPaginaAtual === totalRunsPages}
                    aria-label="Última página"
                  >
                    »
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="automacao-logs">
            <h2>Logs {selectedRunId ? `#${selectedRunId}` : ''}</h2>
            {!selectedRunId ? (
              <p>Selecione uma execução para ver os logs.</p>
            ) : logs.length === 0 ? (
              <p>Nenhum log.</p>
            ) : (
              <>
                <div className="automacao-logs-pagination-info">
                  <span>
                    Mostrando {((paginaAtual - 1) * LOGS_POR_PAGINA) + 1} a{' '}
                    {Math.min(paginaAtual * LOGS_POR_PAGINA, logs.length)} de {logsTotal} logs
                    {logs.length < logsTotal ? ` (${logs.length} carregados)` : ''}
                  </span>
                  <span className="automacao-logs-per-page">{LOGS_POR_PAGINA} por página</span>
                </div>
                <div className="automacao-log-list">
                  {logsPaginados.map((log) => (
                    <div key={log.id} className={`automacao-log-item nivel-${log.nivel}`}>
                      <div className="automacao-log-head">
                        <span className="log-time">{formatDate(log.created_at)}</span>
                        {log.passo && <span className="log-passo">[{log.passo}]</span>}
                        {log.sale_id && <span className="log-venda">Venda #{log.sale_id}</span>}
                      </div>
                      <span className="log-msg">{log.mensagem}</span>
                      {log.screenshot_path && (
                        <div className="automacao-log-screenshot">
                          <a
                            href={screenshotUrl(log.screenshot_path)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={screenshotUrl(log.screenshot_path)}
                              alt={`Screenshot ${log.passo || ''}`}
                              loading="lazy"
                            />
                            Abrir em tela cheia
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={logsEndRef} />
                </div>
                <div className="automacao-logs-pagination">
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setLogsPage(1)}
                    disabled={paginaAtual === 1}
                    aria-label="Primeira página"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                    disabled={paginaAtual === 1}
                  >
                    Anterior
                  </button>
                  <span className="automacao-logs-pagination-pages">
                    Página {paginaAtual} de {totalLogsPages}
                  </span>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setLogsPage((p) => Math.min(totalLogsPages, p + 1))}
                    disabled={paginaAtual === totalLogsPages}
                  >
                    Próxima
                  </button>
                  <button
                    type="button"
                    className="btn-sm btn-secondary"
                    onClick={() => setLogsPage(totalLogsPages)}
                    disabled={paginaAtual === totalLogsPages}
                    aria-label="Última página"
                  >
                    »
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AutomacaoValidacaoOk;
